import * as React from "react";
import { CustomerData, CustomerDataFilter, ProjectData } from ".";
import Select from "react-select";
import { SingleValue } from "react-select";
import styled from "styled-components";
import { useEffect, useState } from "react";

interface Props {
  customers: CustomerData[];
  existingFilters: CustomerDataFilter[];
  onAdd: (filter: CustomerDataFilter[]) => void;
  closeModal: () => void;
}

interface CustomerDropdownOption {
  id: number;
  value: string;
  label: string;
}

interface ProjectDropdownOption {
  id: number;
  value: string;
  label: string;
}

const Wrapper = styled.div`
  width: 500px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & > .buttons {
    display: flex;
    gap: 0.5rem;

    & > * {
      flex-grow: 1;
      margin-bottom: 0;
    }
  }

  & .warning {
    color: #f08a24;
    font-size: 0.8rem;
  }
`;

const AddCustomerFilterModal = ({
  customers,
  existingFilters,
  onAdd,
  closeModal,
}: Props) => {
  const [selectedCustomer, setSelectedCustomer] = useState(
    null as CustomerData | null
  );
  const [selectedProject, setSelectedProject] = useState(
    null as ProjectData | null
  );
  const [selectedProjectOption, setSelectedProjectOption] = useState(
    null as ProjectDropdownOption | null
  );
  const [customerOptions, setCustomerOptions] = useState(
    [] as CustomerDropdownOption[]
  );
  const [projectOptions, setProjectOptions] = useState(
    [] as ProjectDropdownOption[]
  );

  // on customers prop changed
  useEffect(() => {
    let newCustomerOptions = customers
      .filter(
        // exclude customers that are already filtered without a project
        (c) => !existingFilters.find((f) => f.id === c.id && f.project == null)
      )
      .map(
        (c) =>
          ({
            id: c.id,
            value: c.id.toString(),
            label: `${c.name} (${c.vismaId})`,
          } as CustomerDropdownOption)
      );
    setCustomerOptions(newCustomerOptions);
    setSelectedCustomer(null);
    setSelectedProject(null);
  }, [customers]);

  // on selected customer changed
  useEffect(() => {
    if (selectedCustomer == null) {
      setProjectOptions([]);
    } else {
      const projectAlreadyAdded = (project: ProjectData): boolean => {
        var matchingFilterCompanies = existingFilters.filter(
          (ef) => ef.id === selectedCustomer.id
        );

        if (!matchingFilterCompanies.length) {
          return false;
        }

        return (
          matchingFilterCompanies.find((fc) => fc.project?.id === project.id) !=
          null
        );
      };

      const newProjectOptions = selectedCustomer.projects
        .filter((p) => !projectAlreadyAdded(p))
        .map(
          (p) =>
            ({
              id: p.id,
              value: p.id.toString(),
              label: p.name,
            } as ProjectDropdownOption)
        );

      setProjectOptions(newProjectOptions);
    }

    setSelectedProject(null);
    setSelectedProjectOption(null);
  }, [selectedCustomer]);

  const onCustomerChanged = (newValue: SingleValue<CustomerDropdownOption>) => {
    if (newValue == null) {
      setSelectedCustomer(null);
      return;
    }

    const customerData = customers.find((c) => c.id === newValue?.id)!;
    setSelectedCustomer(customerData);
  };

  const onProjectChanged = (newValue: SingleValue<ProjectDropdownOption>) => {
    setSelectedProjectOption(newValue);

    if (newValue == null) {
      setSelectedProject(null);
      return;
    }

    const projectData = selectedCustomer?.projects.find(
      (p) => p.id === newValue.id
    );

    setSelectedProject(projectData ?? null);
  };

  const getProjectPlaceholderText = (): string => {
    if (selectedCustomer == null) {
      return "Välj kund först";
    }

    if (!projectOptions.length && !!selectedCustomer.projects.length) {
      return "Alla projekten är redan tillagda";
    }

    if (!projectOptions.length) {
      return "Inga projekt tillgängliga";
    }

    return "Välj projekt...";
  };

  const addFilterAndCloseModal = () => {
    if (selectedCustomer == null) {
      return;
    }

    const customerFilter: CustomerDataFilter = {
      id: selectedCustomer.id,
      name: selectedCustomer.name,
      project: selectedProject ?? undefined,
    };

    onAdd([customerFilter]);
    closeModal();
  };

  const isProjectRequired =
    existingFilters.find((cf) => cf.id === selectedCustomer?.id) != null;

  return (
    <Wrapper>
      <div className="input">
        <div>
          <label>
            <span>Kund</span>
            <Select
              placeholder="Välj kund..."
              options={customerOptions}
              onChange={onCustomerChanged}
              defaultMenuIsOpen={true}
              autoFocus={true}
            />
          </label>
        </div>
        <div>
          <label>
            <span>Projekt {!isProjectRequired && "(valfritt)"}</span>
            <Select
              placeholder={getProjectPlaceholderText()}
              isDisabled={
                selectedCustomer == null ||
                !projectOptions.length ||
                (!projectOptions.length && !!selectedCustomer.projects.length)
              }
              options={projectOptions}
              isClearable={true}
              value={selectedProjectOption}
              onChange={onProjectChanged}
              backspaceRemovesValue={true}
            />
          </label>
          {isProjectRequired && selectedProject == null && (
            <span className="warning">
              Obs: Projekt krävs på grund av att ett filter redan lagts till på
              samma företag.
            </span>
          )}
        </div>
      </div>
      <div className="buttons">
        <button
          className="button primary"
          disabled={
            selectedCustomer == null ||
            (!projectOptions.length && !!selectedCustomer.projects.length) ||
            (selectedProject == null && isProjectRequired)
          }
          onClick={addFilterAndCloseModal}
        >
          Ok
        </button>
        <button className="button secondary" onClick={closeModal}>
          Avbryt
        </button>
      </div>
    </Wrapper>
  );
};

export default AddCustomerFilterModal;
